/**
 * Marketplace API Service
 * 
 * Clean wrapper for the Forge Plugin Marketplace REST API.
 * Provides a consistent interface for all marketplace operations.
 * 
 * Authentication: Bearer token (API key)
 * Base URL: Configured via VUE_APP_MARKETPLACE_API_URL
 * API Key: Configured via VUE_APP_MARKETPLACE_API_KEY
 */

import { MARKETPLACE_API_URL, MARKETPLACE_API_KEY } from '@/env'

class MarketplaceAPIError extends Error {
  constructor(message, status, data) {
    super(message)
    this.name = 'MarketplaceAPIError'
    this.status = status
    this.data = data
  }
}

/**
 * Base API request handler
 */
async function apiRequest(endpoint, options = {}) {
  const baseURL = MARKETPLACE_API_URL || 'http://127.0.0.1:3000'
  const apiKey = MARKETPLACE_API_KEY

  if (!apiKey) {
    throw new MarketplaceAPIError('Marketplace API key not configured', 401)
  }

  const url = `${baseURL}${endpoint}`
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${apiKey}`,
    ...options.headers,
  }

  const config = {
    ...options,
    headers,
  }

  try {
    const response = await fetch(url, config)
    const data = await response.json().catch(() => ({})) // Handle non-JSON responses

    if (!response.ok) {
      // Extract error message from various possible locations in the response
      let errorMessage = 
        data.error || 
        data.message || 
        data.errors?.join(', ') ||
        (data.errors && typeof data.errors === 'object' ? Object.values(data.errors).flat().join(', ') : null) ||
        `API request failed: ${response.statusText}`
      
      throw new MarketplaceAPIError(
        errorMessage,
        response.status,
        data
      )
    }

    return data
  } catch (error) {
    if (error instanceof MarketplaceAPIError) {
      throw error
    }
    throw new MarketplaceAPIError(
      `Network error: ${error.message}`,
      0,
      { originalError: error.message }
    )
  }
}

/**
 * GET request helper
 */
function get(endpoint, params = {}) {
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([_, v]) => v != null && v !== '')
  ).toString()
  const url = queryString ? `${endpoint}?${queryString}` : endpoint
  return apiRequest(url, { method: 'GET' })
}

/**
 * POST request helper
 */
function post(endpoint, body = {}) {
  return apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * PATCH request helper
 */
function patch(endpoint, body = {}) {
  return apiRequest(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

/**
 * DELETE request helper
 */
function del(endpoint) {
  return apiRequest(endpoint, { method: 'DELETE' })
}

/**
 * Marketplace API Service
 */
export const marketplaceAPI = {
  // ============================================
  // Developers
  // ============================================

  /**
   * List developers
   * @param {Object} filters - { verified, status, query }
   * @returns {Promise<Array>} List of developers
   */
  async listDevelopers(filters = {}) {
    const response = await get('/api/v1/developers', filters)
    return response.developers || response.data || []
  },

  /**
   * Get developer by ID
   * @param {number|string} id - Developer ID
   * @returns {Promise<Object>} Developer details
   */
  async getDeveloper(id) {
    return get(`/api/v1/developers/${id}`)
  },

  /**
   * Find developer by email
   * @param {string} email - Developer email
   * @returns {Promise<Object|null>} Developer or null if not found
   */
  async findDeveloperByEmail(email) {
    const response = await get('/api/v1/developers/find_by_email', { email })
    // Handle null response (developer not found)
    if (!response) return null
    // API may return developer directly or wrapped in developer/data property
    // Check if response has developer properties (id, email, etc.) to determine if it's the developer object itself
    if (response.id && response.email) {
      return response
    }
    return response.developer || response.data || null
  },

  /**
   * Create developer
   * @param {Object} developerData - Developer information
   * @returns {Promise<Object>} Created developer
   */
  async createDeveloper(developerData) {
    return post('/api/v1/developers', { developer: developerData })
  },

  /**
   * Update developer
   * @param {number|string} id - Developer ID
   * @param {Object} developerData - Updated developer information
   * @returns {Promise<Object>} Updated developer
   */
  async updateDeveloper(id, developerData) {
    return patch(`/api/v1/developers/${id}`, { developer: developerData })
  },

  /**
   * Delete developer
   * @param {number|string} id - Developer ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteDeveloper(id) {
    return del(`/api/v1/developers/${id}`)
  },

  // ============================================
  // Plugins
  // ============================================

  /**
   * List plugins
   * @param {Object} filters - { status, query }
   * @returns {Promise<Array>} List of plugins
   */
  async listPlugins(filters = {}) {
    const response = await get('/api/v1/plugins', filters)
    return response.plugins || response.data || []
  },

  /**
   * Get plugin by slug
   * @param {string} slug - Plugin slug
   * @returns {Promise<Object>} Plugin details
   */
  async getPlugin(slug) {
    return get(`/api/v1/plugins/${slug}`)
  },

  /**
   * Get plugin versions
   * @param {string} slug - Plugin slug
   * @returns {Promise<Array>} List of versions
   */
  async getPluginVersions(slug) {
    const response = await get(`/api/v1/plugins/${slug}/versions`)
    return response.versions || response.data || []
  },

  /**
   * Create plugin
   * @param {number} developerId - Developer ID
   * @param {Object} pluginData - Plugin information
   * @returns {Promise<Object>} Created plugin
   */
  async createPlugin(developerId, pluginData) {
    return post('/api/v1/plugins', {
      developer_id: developerId,
      plugin: pluginData,
    })
  },

  /**
   * Update plugin
   * @param {string} slug - Plugin slug
   * @param {number} developerId - Developer ID
   * @param {Object} pluginData - Updated plugin information
   * @returns {Promise<Object>} Updated plugin
   */
  async updatePlugin(slug, developerId, pluginData) {
    return patch(`/api/v1/plugins/${slug}`, {
      developer_id: developerId,
      plugin: pluginData,
    })
  },

  /**
   * Delete plugin (soft delete)
   * @param {string} slug - Plugin slug
   * @param {number} developerId - Developer ID
   * @returns {Promise<Object>} Deletion result
   */
  async deletePlugin(slug, developerId) {
    return del(`/api/v1/plugins/${slug}?developer_id=${developerId}`)
  },

  // ============================================
  // Installations
  // ============================================

  /**
   * List installations
   * @param {Object} filters - { scope_type, scope_id, status, plugin_id, plugin_version_id, organization_id, created_after, created_before }
   * @returns {Promise<Array>} List of installations
   */
  async listInstallations(filters = {}) {
    const response = await get('/api/v1/installations', filters)
    return response.installations || response.data || []
  },

  /**
   * Get installation by ID
   * @param {number|string} id - Installation ID
   * @returns {Promise<Object>} Installation details
   */
  async getInstallation(id) {
    return get(`/api/v1/installations/${id}`)
  },

  /**
   * Get installation analytics
   * @param {Object} filters - { time_period, start_date, end_date, plugin_id, status }
   * @returns {Promise<Object>} Analytics data
   */
  async getInstallationAnalytics(filters = {}) {
    return get('/api/v1/installations/analytics', filters)
  },

  /**
   * Create installation
   * @param {Object} params - { plugin_id, plugin_version_id, organization_id, user_id, installed_by_user_id, developer_id, installation: { install_scope_type, install_scope_id, status, config } }
   * @returns {Promise<Object>} Created installation
   */
  async createInstallation(params) {
    const { plugin_id, plugin_version_id, organization_id, user_id, installed_by_user_id, developer_id, installation } = params
    const body = { installation }
    if (plugin_version_id) body.plugin_version_id = plugin_version_id
    if (plugin_id) body.plugin_id = plugin_id
    if (organization_id) body.organization_id = organization_id
    // Prefer user_id (Forge user ID) over developer_id for draft plugin installations
    if (user_id) {
      body.user_id = user_id
    } else if (installed_by_user_id) {
      body.installed_by_user_id = installed_by_user_id
    } else if (developer_id) {
      // Legacy fallback - use developer_id if user_id not available
      body.developer_id = developer_id
    }
    return post('/api/v1/installations', body)
  },

  /**
   * Update installation
   * @param {number|string} id - Installation ID
   * @param {Object} installationData - Updated installation data
   * @returns {Promise<Object>} Updated installation
   */
  async updateInstallation(id, installationData) {
    return patch(`/api/v1/installations/${id}`, { installation: installationData })
  },

  /**
   * Enable installation
   * @param {number|string} id - Installation ID
   * @returns {Promise<Object>} Updated installation
   */
  async enableInstallation(id) {
    return post(`/api/v1/installations/${id}/enable`)
  },

  /**
   * Disable installation
   * @param {number|string} id - Installation ID
   * @returns {Promise<Object>} Updated installation
   */
  async disableInstallation(id) {
    return post(`/api/v1/installations/${id}/disable`)
  },

  /**
   * Delete installation
   * @param {number|string} id - Installation ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteInstallation(id) {
    return del(`/api/v1/installations/${id}`)
  },

  /**
   * Get installation by scope (site or user)
   * Helper method to find installation by scope
   * @param {string} scopeType - 'site' or 'user'
   * @param {string} scopeId - Scope ID (e.g., site ID or user ID)
   * @param {string} pluginSlug - Optional plugin slug to filter
   * @returns {Promise<Object|null>} Installation or null
   */
  async getInstallationByScope(scopeType, scopeId, pluginSlug = null) {
    const filters = {
      scope_type: scopeType,
      scope_id: scopeId,
      status: 'active',
    }
    const installations = await this.listInstallations(filters)
    
    if (pluginSlug) {
      return installations.find(
        (inst) => inst.plugin?.slug === pluginSlug || inst.plugin_slug === pluginSlug
      ) || null
    }
    
    return installations.length > 0 ? installations : null
  },

  // ============================================
  // Plugin Versions
  // ============================================

  /**
   * Refresh analysis for a plugin version
   * @param {number|string} versionId - Plugin version ID
   * @returns {Promise<Object>} Job status
   */
  async refreshVersionAnalysis(versionId) {
    return post(`/api/v1/plugin_versions/${versionId}/analysis_refresh`)
  },

  /**
   * Get analysis reports for a plugin version
   * @param {number|string} versionId - Plugin version ID
   * @param {string} kind - Analysis kind (static, runtime_snapshot, runtime_window)
   * @returns {Promise<Object>} Analysis reports response with reports array
   */
  async getVersionAnalysisReports(versionId, kind = 'static') {
    const response = await get(`/api/v1/plugin_versions/${versionId}/analysis_reports`, { kind })
    // Response format: { plugin_version_id, kind, count, reports: [...] }
    return response
  },

  /**
   * Evaluate policies for a plugin version (triggers new evaluation)
   * @param {number|string} versionId - Plugin version ID
   * @param {Array} policySetIds - Optional array of policy set IDs to evaluate against
   * @returns {Promise<Object>} Policy evaluation results with evaluations and enforcement_actions
   */
  async evaluateVersionPolicies(versionId, policySetIds = null) {
    const body = policySetIds ? { policy_set_ids: policySetIds } : {}
    return post(`/api/v1/plugin_versions/${versionId}/policy_evaluate`, body)
  },

  /**
   * Get latest policy evaluations for a plugin version
   * @param {number|string} versionId - Plugin version ID
   * @returns {Promise<Object>} Latest evaluations with summary
   */
  async getVersionLatestPolicyEvaluations(versionId) {
    return get(`/api/v1/plugin_versions/${versionId}/policy_evaluations/latest`)
  },

  /**
   * List policy evaluations for a plugin version
   * @param {number|string} versionId - Plugin version ID
   * @param {Object} filters - { latest_only, policy_set_id, result, page, per_page }
   * @returns {Promise<Object>} Evaluations with pagination metadata
   */
  async listVersionPolicyEvaluations(versionId, filters = {}) {
    return get(`/api/v1/plugin_versions/${versionId}/policy_evaluations`, filters)
  },

  /**
   * List all policy evaluations (across all targets)
   * @param {Object} filters - { target_type, target_id, policy_set_id, result, created_after, created_before, page, per_page }
   * @returns {Promise<Object>} Evaluations with pagination metadata
   */
  async listPolicyEvaluations(filters = {}) {
    return get('/api/v1/policy_evaluations', filters)
  },

  /**
   * Get policy evaluation by ID
   * @param {number|string} evaluationId - Policy evaluation ID
   * @returns {Promise<Object>} Evaluation details
   */
  async getPolicyEvaluation(evaluationId) {
    return get(`/api/v1/policy_evaluations/${evaluationId}`)
  },

  // ============================================
  // Plugin Assets
  // ============================================

  /**
   * Upload plugin icon
   * @param {string} slug - Plugin slug
   * @param {File} file - Icon file
   * @param {number} developerId - Developer ID
   * @returns {Promise<Object>} Upload result with URL
   */
  async uploadIcon(slug, file, developerId) {
    return this._uploadAsset(slug, 'icon', file, developerId)
  },

  /**
   * Delete plugin icon
   * @param {string} slug - Plugin slug
   * @param {number} developerId - Developer ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteIcon(slug, developerId) {
    return del(`/api/v1/plugins/${slug}/icon?developer_id=${developerId}`)
  },

  /**
   * Upload plugin thumbnail
   * @param {string} slug - Plugin slug
   * @param {File} file - Thumbnail file
   * @param {number} developerId - Developer ID
   * @returns {Promise<Object>} Upload result with URL
   */
  async uploadThumbnail(slug, file, developerId) {
    return this._uploadAsset(slug, 'thumbnail', file, developerId)
  },

  /**
   * Delete plugin thumbnail
   * @param {string} slug - Plugin slug
   * @param {number} developerId - Developer ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteThumbnail(slug, developerId) {
    return del(`/api/v1/plugins/${slug}/thumbnail?developer_id=${developerId}`)
  },

  /**
   * List plugin screenshots
   * @param {string} slug - Plugin slug
   * @returns {Promise<Array>} List of screenshots
   */
  async listScreenshots(slug) {
    const response = await get(`/api/v1/plugins/${slug}/screenshots`)
    return response.screenshots || response.data || []
  },

  /**
   * Upload plugin screenshots (one or more)
   * @param {string} slug - Plugin slug
   * @param {File|File[]} files - Screenshot file(s)
   * @param {number} developerId - Developer ID
   * @returns {Promise<Object>} Upload result with screenshots array
   */
  async uploadScreenshots(slug, files, developerId) {
    const formData = new FormData()
    
    // Handle both single file and array of files
    const fileArray = Array.isArray(files) ? files : [files]
    fileArray.forEach(file => {
      formData.append('screenshots[]', file)
    })
    formData.append('developer_id', developerId)

    const baseURL = MARKETPLACE_API_URL || 'http://127.0.0.1:3000'
    const apiKey = MARKETPLACE_API_KEY

    if (!apiKey) {
      throw new MarketplaceAPIError('Marketplace API key not configured', 401)
    }

    const url = `${baseURL}/api/v1/plugins/${slug}/screenshots`
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        // Don't set Content-Type - browser will set it with boundary
      },
      body: formData,
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      let errorMessage = 
        data.error || 
        data.message || 
        data.errors?.join(', ') ||
        (data.errors && typeof data.errors === 'object' ? Object.values(data.errors).flat().join(', ') : null) ||
        `Upload failed: ${response.statusText}`
      
      throw new MarketplaceAPIError(errorMessage, response.status, data)
    }

    return data
  },

  /**
   * Delete plugin screenshot
   * @param {string} slug - Plugin slug
   * @param {number} screenshotId - Screenshot ID
   * @param {number} developerId - Developer ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteScreenshot(slug, screenshotId, developerId) {
    return del(`/api/v1/plugins/${slug}/screenshots/${screenshotId}?developer_id=${developerId}`)
  },

  /**
   * Internal helper for uploading assets (icon, thumbnail)
   * @private
   */
  async _uploadAsset(slug, assetType, file, developerId) {
    const formData = new FormData()
    formData.append(assetType, file)
    formData.append('developer_id', developerId)

    const baseURL = MARKETPLACE_API_URL || 'http://127.0.0.1:3000'
    const apiKey = MARKETPLACE_API_KEY

    if (!apiKey) {
      throw new MarketplaceAPIError('Marketplace API key not configured', 401)
    }

    const url = `${baseURL}/api/v1/plugins/${slug}/${assetType}`
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        // Don't set Content-Type - browser will set it with boundary
      },
      body: formData,
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      let errorMessage = 
        data.error || 
        data.message || 
        data.errors?.join(', ') ||
        (data.errors && typeof data.errors === 'object' ? Object.values(data.errors).flat().join(', ') : null) ||
        `Upload failed: ${response.statusText}`
      
      throw new MarketplaceAPIError(errorMessage, response.status, data)
    }

    return data
  },

  // ============================================
  // Asset Guidelines
  // ============================================

  /**
   * Get all asset guidelines
   * @returns {Promise<Object>} Asset guidelines for icon, thumbnail, screenshots
   */
  async getAssetGuidelines() {
    return get('/api/v1/marketplace/asset_guidelines')
  },

  /**
   * Get icon guidelines
   * @returns {Promise<Object>} Icon guidelines
   */
  async getIconGuidelines() {
    return get('/api/v1/marketplace/asset_guidelines/icon')
  },

  /**
   * Get thumbnail guidelines
   * @returns {Promise<Object>} Thumbnail guidelines
   */
  async getThumbnailGuidelines() {
    return get('/api/v1/marketplace/asset_guidelines/thumbnail')
  },

  /**
   * Get screenshot guidelines
   * @returns {Promise<Object>} Screenshot guidelines
   */
  async getScreenshotGuidelines() {
    return get('/api/v1/marketplace/asset_guidelines/screenshots')
  },

  // ============================================
  // Policy Sets
  // ============================================

  /**
   * List policy sets
   * @param {Object} filters - { active, scope, query }
   * @returns {Promise<Array>} List of policy sets
   */
  async listPolicySets(filters = {}) {
    const response = await get('/api/v1/policy_sets', filters)
    return response.policy_sets || response.data || []
  },

  /**
   * Get policy set by ID
   * @param {number|string} id - Policy set ID
   * @returns {Promise<Object>} Policy set details with rules
   */
  async getPolicySet(id) {
    return get(`/api/v1/policy_sets/${id}`)
  },

  /**
   * Create policy set
   * @param {Object} policySetData - Policy set information
   * @returns {Promise<Object>} Created policy set
   */
  async createPolicySet(policySetData) {
    return post('/api/v1/policy_sets', { policy_set: policySetData })
  },

  /**
   * Update policy set
   * @param {number|string} id - Policy set ID
   * @param {Object} policySetData - Updated policy set information
   * @returns {Promise<Object>} Updated policy set
   */
  async updatePolicySet(id, policySetData) {
    return patch(`/api/v1/policy_sets/${id}`, { policy_set: policySetData })
  },

  /**
   * Delete policy set
   * @param {number|string} id - Policy set ID
   * @returns {Promise<Object>} Deletion result
   */
  async deletePolicySet(id) {
    return del(`/api/v1/policy_sets/${id}`)
  },

  /**
   * List policy rules for a policy set
   * @param {number|string} policySetId - Policy set ID
   * @returns {Promise<Array>} List of policy rules
   */
  async listPolicyRules(policySetId) {
    const response = await get(`/api/v1/policy_sets/${policySetId}/policy_rules`)
    return response.policy_rules || response.data || []
  },

  /**
   * Get policy rule by ID
   * @param {number|string} policySetId - Policy set ID
   * @param {number|string} ruleId - Policy rule ID
   * @returns {Promise<Object>} Policy rule details
   */
  async getPolicyRule(policySetId, ruleId) {
    return get(`/api/v1/policy_sets/${policySetId}/policy_rules/${ruleId}`)
  },

  /**
   * Create policy rule for a policy set
   * @param {number|string} policySetId - Policy set ID
   * @param {Object} ruleData - Policy rule data { rule_type, severity, config }
   * @returns {Promise<Object>} Created policy rule
   */
  async createPolicyRule(policySetId, ruleData) {
    return post(`/api/v1/policy_sets/${policySetId}/policy_rules`, {
      policy_rule: ruleData,
    })
  },

  /**
   * Update policy rule
   * @param {number|string} policySetId - Policy set ID
   * @param {number|string} ruleId - Policy rule ID
   * @param {Object} ruleData - Updated policy rule data
   * @returns {Promise<Object>} Updated policy rule
   */
  async updatePolicyRule(policySetId, ruleId, ruleData) {
    return patch(`/api/v1/policy_sets/${policySetId}/policy_rules/${ruleId}`, {
      policy_rule: ruleData,
    })
  },

  /**
   * Delete policy rule
   * @param {number|string} policySetId - Policy set ID
   * @param {number|string} ruleId - Policy rule ID
   * @returns {Promise<Object>} Deletion result
   */
  async deletePolicyRule(policySetId, ruleId) {
    return del(`/api/v1/policy_sets/${policySetId}/policy_rules/${ruleId}`)
  },

  // ============================================
  // Reports
  // ============================================

  /**
   * List available report types
   * @returns {Promise<Object>} Available report types with descriptions and filters
   */
  async listAvailableReports() {
    return get('/api/v1/reports')
  },

  /**
   * Generate Top Developers Report
   * @param {Object} options - Report generation options
   * @param {Object} options.filters - { time_range, start_date, end_date, developer_status, verified_status, top_n }
   * @param {Object} options.sort - { field, direction }
   * @param {Object} options.pagination - { page, per_page }
   * @param {boolean} options.generate_pdf - Whether to generate PDF asynchronously
   * @returns {Promise<Object>} Report generation result with data and generation ID
   */
  async generateTopDevelopersReport(options = {}) {
    const {
      filters = {},
      sort = {},
      pagination = {},
      generate_pdf = false,
    } = options

    return post('/api/v1/reports/top_developers/generate', {
      filters,
      sort,
      pagination,
      generate_pdf,
    })
  },

  /**
   * Generate Plugin Installs Report
   * @param {Object} options - Report generation options
   * @param {Object} options.filters - { time_range, start_date, end_date, plugin_status, ready_for_sale_only, hide_zero_installs, categories, tags, developer_ids }
   * @param {Object} options.sort - { field, direction }
   * @param {Object} options.pagination - { page, per_page }
   * @param {boolean} options.generate_pdf - Whether to generate PDF asynchronously
   * @returns {Promise<Object>} Report generation result with data and generation ID
   */
  async generatePluginInstallsReport(options = {}) {
    const {
      filters = {},
      sort = {},
      pagination = {},
      generate_pdf = false,
    } = options

    return post('/api/v1/reports/plugin_installs/generate', {
      filters,
      sort,
      pagination,
      generate_pdf,
    })
  },

  /**
   * Refresh materialized views used for reports
   * @returns {Promise<Object>} Refresh status
   */
  async refreshReportViews() {
    return post('/api/v1/reports/refresh_views')
  },

  /**
   * List report generations
   * @param {Object} filters - { page, per_page, report_type, status }
   * @returns {Promise<Object>} List of report generations with pagination
   */
  async listReportGenerations(filters = {}) {
    return get('/api/v1/report_generations', filters)
  },

  /**
   * Get report generation by ID
   * @param {number|string} id - Report generation ID
   * @returns {Promise<Object>} Report generation details
   */
  async getReportGeneration(id) {
    return get(`/api/v1/report_generations/${id}`)
  },

  /**
   * Download report PDF
   * @param {number|string} id - Report generation ID
   * @returns {Promise<Blob>} PDF file blob
   */
  async downloadReportPDF(id) {
    const baseURL = MARKETPLACE_API_URL || 'http://127.0.0.1:3000'
    const apiKey = MARKETPLACE_API_KEY

    if (!apiKey) {
      throw new MarketplaceAPIError('Marketplace API key not configured', 401)
    }

    const url = `${baseURL}/api/v1/report_generations/${id}/download`
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      let errorMessage =
        data.error ||
        data.message ||
        `PDF download failed: ${response.statusText}`
      throw new MarketplaceAPIError(errorMessage, response.status, data)
    }

    return response.blob()
  },

  // ============================================
  // Reviews (User/Public)
  // ============================================

  /**
   * Create a review for a plugin
   * @param {Object} reviewData - { plugin_slug, rating, comment, author_name, author_email }
   * @returns {Promise<Object>} Created review
   */
  async createReview(reviewData) {
    return post('/api/v1/reviews', reviewData)
  },

  /**
   * Get a review by ID
   * @param {number|string} id - Review ID
   * @returns {Promise<Object>} Review details
   */
  async getReview(id) {
    return get(`/api/v1/reviews/${id}`)
  },

  /**
   * Update a review (user's own review)
   * @param {number|string} id - Review ID
   * @param {Object} reviewData - { rating, comment }
   * @returns {Promise<Object>} Updated review
   */
  async updateReview(id, reviewData) {
    return patch(`/api/v1/reviews/${id}`, reviewData)
  },

  /**
   * Delete a review (user's own review)
   * @param {number|string} id - Review ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteReview(id) {
    return del(`/api/v1/reviews/${id}`)
  },

  /**
   * Get current user's reviews
   * @param {Object} filters - { status, page, per_page }
   * @returns {Promise<Object>} Reviews with pagination
   */
  async getMyReviews(filters = {}) {
    return get('/api/v1/reviews/my', filters)
  },

  /**
   * List reviews for a plugin (developer-only)
   * @param {string} pluginSlug - Plugin slug
   * @param {Object} filters - { status, rating, sort, page, per_page }
   * @returns {Promise<Object>} Reviews with plugin summary and statistics
   */
  async listPluginReviews(pluginSlug, filters = {}) {
    return get(`/api/v1/plugins/${pluginSlug}/reviews`, filters)
  },

  /**
   * Get review statistics for a plugin (developer-only)
   * @param {string} pluginSlug - Plugin slug
   * @returns {Promise<Object>} Review statistics
   */
  async getPluginReviewStatistics(pluginSlug) {
    return get(`/api/v1/plugins/${pluginSlug}/reviews/statistics`)
  },

  // ============================================
  // Admin Reviews
  // ============================================

  /**
   * List all reviews (admin-only)
   * @param {Object} filters - { status, rating, plugin_slug, author_email, author_name, search, sort, page, per_page }
   * @returns {Promise<Object>} Reviews with pagination and statistics
   */
  async listAdminReviews(filters = {}) {
    return get('/api/v1/admin/reviews', filters)
  },

  /**
   * Get review details (admin-only)
   * @param {number|string} id - Review ID
   * @returns {Promise<Object>} Review details with admin fields
   */
  async getAdminReview(id) {
    return get(`/api/v1/admin/reviews/${id}`)
  },

  /**
   * Approve a review (admin-only)
   * @param {number|string} id - Review ID
   * @param {string} adminNotes - Optional admin notes
   * @returns {Promise<Object>} Updated review
   */
  async approveReview(id, adminNotes = '') {
    return patch(`/api/v1/admin/reviews/${id}/approve`, { admin_notes: adminNotes })
  },

  /**
   * Decline a review (admin-only)
   * @param {number|string} id - Review ID
   * @param {string} adminNotes - Admin notes explaining reason for decline
   * @returns {Promise<Object>} Updated review
   */
  async declineReview(id, adminNotes = '') {
    return patch(`/api/v1/admin/reviews/${id}/decline`, { admin_notes: adminNotes })
  },

  /**
   * Update a review (admin-only)
   * @param {number|string} id - Review ID
   * @param {Object} reviewData - { rating, comment, admin_notes }
   * @returns {Promise<Object>} Updated review
   */
  async updateAdminReview(id, reviewData) {
    return patch(`/api/v1/admin/reviews/${id}`, reviewData)
  },

  /**
   * Delete a review (admin-only)
   * @param {number|string} id - Review ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteAdminReview(id) {
    return del(`/api/v1/admin/reviews/${id}`)
  },
}

export default marketplaceAPI
export { MarketplaceAPIError }

